import React, { useState } from "react"
import { Alert, Col, Label, Row, TabPane } from "reactstrap"
import { useLoading } from "helpers/custom_hooks"
import { SaveTraderSelfDisclosure } from "helpers/backend_helper"
import { displayMessage } from "helpers/misc_helpers"

const SOURCING_FOCUS_OPTIONS = [
  "Excess Inventory",
  "Allocation Support",
  "Obsolete & EOL",
  "Broker Business",
]

const TESTING_SERVICE_OPTIONS = [
  "Visual / dimensional inspection",
  "XRF analysis",
  "Decapsulation",
  "SEM analysis",
  "X-ray inspection",
  "Solderability testing",
  "Date code / lot verification",
  "Counterfeit detection",
]

const CERTIFICATION_OPTIONS = [
  "ISO 9001",
  "AS9120",
  "ISO 14001",
  "ANSI/ESD S20.20",
  "IDEA-STD-1010",
]

const emptyRevenueRegionRow = { region: "", revenue: "", share: "" }
const emptyRevenueCategoryRow = { category: "", revenue: "", share: "" }
const emptyManufacturerRow = { manufacturer: "", share: "", franchised: "" }
const emptyFranchiseRow = { manufacturer: "", region: "", since: "" }

const initialData = {
  additionalLocations: "",
  totalEmployees: "",
  warehouseSpaceM2: "",
  storageCapacity: "",
  totalRevenueLastYear: "",
  revenueByRegion: [{ ...emptyRevenueRegionRow }],
  revenueByCategory: [{ ...emptyRevenueCategoryRow }],
  topManufacturers: [{ ...emptyManufacturerRow }],
  sourcingFocus: [],
  franchisedManufacturers: [{ ...emptyFranchiseRow }],
  noFranchiseBusiness: false,
  numberOfActiveSuppliers: "",
  averageLeadTimeDays: "",
  ownTestLab: "",
  testLabLocations: "",
  testingServices: [],
  otherTestingServices: "",
  externalPartnerLabs: "",
  externalPartnerLabsWhich: "",
  certifications: [],
  otherCertifications: "",
  eraiIdeaMembership: "",
  traceabilityScope: "",
  antiCounterfeitingPolicy: "",
  conflictMineralsPolicy: "",
  erpSystem: "",
  ediConnection: "",
  apiInterface: "",
  standardPaymentTerms: "",
  acceptedPaymentMethods: "",
  standardIncoterms: "",
  moq: "",
  referenceCustomers: "",
  industryAssociations: "",
  additionalComments: "",
}

const YesNo = ({ name, value, onChange }) => (
  <div className="mb-3">
    {["Yes", "No"].map(option => (
      <div className="form-check form-check-inline" key={option}>
        <input
          type="radio"
          className="form-check-input"
          name={name}
          id={`${name}-${option}`}
          checked={value === option}
          onChange={() => onChange(option)}
        />
        <label className="form-check-label" htmlFor={`${name}-${option}`}>
          {option}
        </label>
      </div>
    ))}
  </div>
)

const CheckboxGroup = ({ options, values, onChange }) => (
  <div className="mb-3">
    {options.map(option => (
      <div className="form-check" key={option}>
        <input
          type="checkbox"
          className="form-check-input"
          id={`chk-${option}`}
          checked={values.includes(option)}
          onChange={e => {
            if (e.target.checked) onChange([...values, option])
            else onChange(values.filter(v => v !== option))
          }}
        />
        <label className="form-check-label" htmlFor={`chk-${option}`}>
          {option}
        </label>
      </div>
    ))}
  </div>
)

const RowsTable = ({ headers, rows, columns, emptyRow, onChange }) => {
  const updateCell = (idx, key, value) => {
    const next = rows.map((row, i) => (i === idx ? { ...row, [key]: value } : row))
    onChange(next)
  }

  return (
    <div className="table-responsive mb-3">
      <table className="table table-sm">
        <thead>
          <tr>
            {headers.map(h => (
              <th key={h}>{h}</th>
            ))}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {columns.map(col => (
                <td key={col}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={row[col] ?? ""}
                    onChange={e => updateCell(idx, col, e.target.value)}
                  />
                </td>
              ))}
              <td>
                {rows.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => onChange(rows.filter((_, i) => i !== idx))}
                  >
                    <i className="bx bx-trash" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        onClick={() => onChange([...rows, { ...emptyRow }])}
      >
        <i className="bx bx-plus me-1" /> Add row
      </button>
    </div>
  )
}

const SelfDisclosureTab = ({ nextTab, businessType }) => {
  const setLoading = useLoading()
  const [error, setError] = useState()
  const [data, setData] = useState(initialData)

  const set = (key, value) => setData(prev => ({ ...prev, [key]: value }))

  const handleSubmit = async e => {
    e.preventDefault()
    try {
      setLoading(true)
      await SaveTraderSelfDisclosure(data)
      nextTab()
    } catch (error) {
      if (error?.response?.status === 400)
        displayMessage(error.response.data, setError)
      else displayMessage("An unexpected error happened!", setError)
      console.error(error)
    }
    setLoading(false)
  }

  if (businessType !== "Trader") {
    return (
      <TabPane tabId={3}>
        <div className="d-flex justify-content-end">
          <button className="btn btn-primary" onClick={nextTab}>
            Next
          </button>
        </div>
      </TabPane>
    )
  }

  return (
    <TabPane tabId={3}>
      {error && (
        <Alert color="danger" style={{ marginTop: "13px" }}>
          {error}
        </Alert>
      )}
      <p className="text-muted">
        As a trading partner, please help us assess your company by
        completing this self-disclosure questionnaire. All information is
        treated confidentially and used solely for partner qualification.
      </p>
      <form onSubmit={handleSubmit}>
        <h6 className="card-title mt-4 mb-3">1. Company Information</h6>
        <Row>
          <Col lg="12">
            <div className="mb-3">
              <Label>Additional locations / branches:</Label>
              <textarea
                className="form-control"
                rows={2}
                value={data.additionalLocations}
                onChange={e => set("additionalLocations", e.target.value)}
              />
            </div>
          </Col>
          <Col lg="4">
            <div className="mb-3">
              <Label>Total employees worldwide:</Label>
              <input
                type="text"
                className="form-control"
                value={data.totalEmployees}
                onChange={e => set("totalEmployees", e.target.value)}
              />
            </div>
          </Col>
          <Col lg="4">
            <div className="mb-3">
              <Label>Total warehouse space (m²):</Label>
              <input
                type="text"
                className="form-control"
                value={data.warehouseSpaceM2}
                onChange={e => set("warehouseSpaceM2", e.target.value)}
              />
            </div>
          </Col>
          <Col lg="4">
            <div className="mb-3">
              <Label>Storage capacity (slots / components):</Label>
              <input
                type="text"
                className="form-control"
                value={data.storageCapacity}
                onChange={e => set("storageCapacity", e.target.value)}
              />
            </div>
          </Col>
        </Row>

        <h6 className="card-title mt-4 mb-3">2. Revenue &amp; Business Volume</h6>
        <div className="mb-3">
          <Label>Total revenue last fiscal year (€):</Label>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 300 }}
            value={data.totalRevenueLastYear}
            onChange={e => set("totalRevenueLastYear", e.target.value)}
          />
        </div>
        <Label>Revenue by region:</Label>
        <RowsTable
          headers={["Region", "Revenue (€)", "Share (%)"]}
          columns={["region", "revenue", "share"]}
          rows={data.revenueByRegion}
          emptyRow={emptyRevenueRegionRow}
          onChange={rows => set("revenueByRegion", rows)}
        />
        <Label>Revenue by component category:</Label>
        <RowsTable
          headers={["Component Category", "Revenue (€)", "Share (%)"]}
          columns={["category", "revenue", "share"]}
          rows={data.revenueByCategory}
          emptyRow={emptyRevenueCategoryRow}
          onChange={rows => set("revenueByCategory", rows)}
        />
        <Label>Top manufacturers by revenue share:</Label>
        <RowsTable
          headers={["Manufacturer", "Share of Revenue (%)", "Franchised Partner (Yes/No)"]}
          columns={["manufacturer", "share", "franchised"]}
          rows={data.topManufacturers}
          emptyRow={emptyManufacturerRow}
          onChange={rows => set("topManufacturers", rows)}
        />

        <h6 className="card-title mt-4 mb-3">3. Sourcing Profile</h6>
        <Label>Business focus (multiple selection):</Label>
        <CheckboxGroup
          options={SOURCING_FOCUS_OPTIONS}
          values={data.sourcingFocus}
          onChange={v => set("sourcingFocus", v)}
        />
        <div className="form-check mb-3">
          <input
            type="checkbox"
            className="form-check-input"
            id="noFranchiseBusiness"
            checked={data.noFranchiseBusiness}
            onChange={e => set("noFranchiseBusiness", e.target.checked)}
          />
          <label className="form-check-label" htmlFor="noFranchiseBusiness">
            No franchise business – independent/open-market distribution only
          </label>
        </div>
        {!data.noFranchiseBusiness && (
          <>
            <Label>Franchised Manufacturers:</Label>
            <RowsTable
              headers={["Manufacturer", "Contract Region / Territory", "Partner Since (Year)"]}
              columns={["manufacturer", "region", "since"]}
              rows={data.franchisedManufacturers}
              emptyRow={emptyFranchiseRow}
              onChange={rows => set("franchisedManufacturers", rows)}
            />
          </>
        )}
        <Row>
          <Col lg="6">
            <div className="mb-3">
              <Label>Number of active suppliers worldwide:</Label>
              <input
                type="text"
                className="form-control"
                value={data.numberOfActiveSuppliers}
                onChange={e => set("numberOfActiveSuppliers", e.target.value)}
              />
            </div>
          </Col>
          <Col lg="6">
            <div className="mb-3">
              <Label>Average lead time (days):</Label>
              <input
                type="text"
                className="form-control"
                value={data.averageLeadTimeDays}
                onChange={e => set("averageLeadTimeDays", e.target.value)}
              />
            </div>
          </Col>
        </Row>

        <h6 className="card-title mt-4 mb-3">4. Quality Assurance &amp; Test Laboratories</h6>
        <Label>Own test laboratory available?</Label>
        <YesNo
          name="ownTestLab"
          value={data.ownTestLab}
          onChange={v => set("ownTestLab", v)}
        />
        {data.ownTestLab === "Yes" && (
          <>
            <div className="mb-3">
              <Label>Location(s):</Label>
              <input
                type="text"
                className="form-control"
                value={data.testLabLocations}
                onChange={e => set("testLabLocations", e.target.value)}
              />
            </div>
            <Label>Testing services offered:</Label>
            <CheckboxGroup
              options={TESTING_SERVICE_OPTIONS}
              values={data.testingServices}
              onChange={v => set("testingServices", v)}
            />
            <div className="mb-3">
              <Label>Other testing services:</Label>
              <input
                type="text"
                className="form-control"
                value={data.otherTestingServices}
                onChange={e => set("otherTestingServices", e.target.value)}
              />
            </div>
          </>
        )}
        <Label>External partner laboratories used?</Label>
        <YesNo
          name="externalPartnerLabs"
          value={data.externalPartnerLabs}
          onChange={v => set("externalPartnerLabs", v)}
        />
        {data.externalPartnerLabs === "Yes" && (
          <div className="mb-3">
            <Label>Which ones:</Label>
            <input
              type="text"
              className="form-control"
              value={data.externalPartnerLabsWhich}
              onChange={e => set("externalPartnerLabsWhich", e.target.value)}
            />
          </div>
        )}
        <Label>Certifications:</Label>
        <CheckboxGroup
          options={CERTIFICATION_OPTIONS}
          values={data.certifications}
          onChange={v => set("certifications", v)}
        />
        <div className="mb-3">
          <Label>Other certifications:</Label>
          <input
            type="text"
            className="form-control"
            value={data.otherCertifications}
            onChange={e => set("otherCertifications", e.target.value)}
          />
        </div>
        <Label>Membership in ERAI and/or IDEA?</Label>
        <YesNo
          name="eraiIdeaMembership"
          value={data.eraiIdeaMembership}
          onChange={v => set("eraiIdeaMembership", v)}
        />
        <div className="mb-3">
          <Label>Scope of traceability:</Label>
          <input
            type="text"
            className="form-control"
            value={data.traceabilityScope}
            onChange={e => set("traceabilityScope", e.target.value)}
          />
        </div>

        <h6 className="card-title mt-4 mb-3">5. Compliance &amp; Sustainability</h6>
        <Label>Anti-counterfeiting policy in place?</Label>
        <YesNo
          name="antiCounterfeitingPolicy"
          value={data.antiCounterfeitingPolicy}
          onChange={v => set("antiCounterfeitingPolicy", v)}
        />
        <Label>Conflict minerals policy (CMRT) in place?</Label>
        <YesNo
          name="conflictMineralsPolicy"
          value={data.conflictMineralsPolicy}
          onChange={v => set("conflictMineralsPolicy", v)}
        />

        <h6 className="card-title mt-4 mb-3">6. Digitalization &amp; System Integration</h6>
        <div className="mb-3">
          <Label>ERP system in use:</Label>
          <input
            type="text"
            className="form-control"
            value={data.erpSystem}
            onChange={e => set("erpSystem", e.target.value)}
          />
        </div>
        <Label>EDI connection possible?</Label>
        <YesNo
          name="ediConnection"
          value={data.ediConnection}
          onChange={v => set("ediConnection", v)}
        />
        <Label>API interface available?</Label>
        <YesNo
          name="apiInterface"
          value={data.apiInterface}
          onChange={v => set("apiInterface", v)}
        />

        <h6 className="card-title mt-4 mb-3">7. Trading Terms</h6>
        <Row>
          <Col lg="6">
            <div className="mb-3">
              <Label>Standard payment terms:</Label>
              <input
                type="text"
                className="form-control"
                value={data.standardPaymentTerms}
                onChange={e => set("standardPaymentTerms", e.target.value)}
              />
            </div>
          </Col>
          <Col lg="6">
            <div className="mb-3">
              <Label>Accepted payment methods:</Label>
              <input
                type="text"
                className="form-control"
                value={data.acceptedPaymentMethods}
                onChange={e => set("acceptedPaymentMethods", e.target.value)}
              />
            </div>
          </Col>
          <Col lg="6">
            <div className="mb-3">
              <Label>Standard Incoterms:</Label>
              <input
                type="text"
                className="form-control"
                value={data.standardIncoterms}
                onChange={e => set("standardIncoterms", e.target.value)}
              />
            </div>
          </Col>
          <Col lg="6">
            <div className="mb-3">
              <Label>Minimum order quantity (MOQ):</Label>
              <input
                type="text"
                className="form-control"
                value={data.moq}
                onChange={e => set("moq", e.target.value)}
              />
            </div>
          </Col>
        </Row>

        <h6 className="card-title mt-4 mb-3">8. References &amp; Other Information</h6>
        <div className="mb-3">
          <Label>Reference customers (optional):</Label>
          <input
            type="text"
            className="form-control"
            value={data.referenceCustomers}
            onChange={e => set("referenceCustomers", e.target.value)}
          />
        </div>
        <div className="mb-3">
          <Label>Industry association memberships:</Label>
          <input
            type="text"
            className="form-control"
            value={data.industryAssociations}
            onChange={e => set("industryAssociations", e.target.value)}
          />
        </div>
        <div className="mb-3">
          <Label>Additional comments:</Label>
          <textarea
            className="form-control"
            rows={3}
            value={data.additionalComments}
            onChange={e => set("additionalComments", e.target.value)}
          />
        </div>

        <div className="d-flex justify-content-end">
          <button type="submit" className="btn btn-primary">
            Next
          </button>
        </div>
      </form>
    </TabPane>
  )
}

export default SelfDisclosureTab
